/* eslint-disable no-console */

/**
 * Notification Service Utility
 * Handles toast notifications and user feedback
 */

export class NotificationService {
  constructor () {
    this.toasts = [];
    this.maxToasts = 5;
    this.defaultTimeout = 5000;
    this.container = null;
    this.notificationTypes = {
      success: {
        className: 'toast-success',
        icon: '✓',
        timeout: 3000
      },
      error: {
        className: 'toast-error',
        icon: '✕',
        timeout: 7000
      },
      warning: {
        className: 'toast-warning',
        icon: '⚠',
        timeout: 5000
      },
      info: {
        className: 'toast-info',
        icon: 'ℹ',
        timeout: 4000
      }
    };
  }

  /**
   * Initialize notification service
   */
  init () {
    try {
      console.log('🔔 Initializing Notification Service...');

      // Create toast container
      this.createToastContainer();

      console.log('✅ Notification Service initialized');
      return true;
    } catch (error) {
      console.error('❌ Notification initialization failed:', error);
      return false;
    }
  }

  /**
   * Create toast container element
   */
  createToastContainer () {
    if (typeof document !== 'undefined') {
      // Remove existing container if present
      const existingContainer = document.getElementById('toast-container');
      if (existingContainer) {
        existingContainer.remove();
      }

      // Create new container
      this.container = document.createElement('div');
      this.container.id = 'toast-container';
      this.container.className = 'toast-container';
      document.body.appendChild(this.container);
    }
  }

  /**
   * Show success notification
   */
  success (message, options = {}) {
    return this.show(message, 'success', options);
  }

  /**
   * Show error notification
   */
  error (message, options = {}) {
    return this.show(message, 'error', options);
  }

  /**
   * Show warning notification
   */
  warning (message, options = {}) {
    return this.show(message, 'warning', options);
  }

  /**
   * Show info notification
   */
  info (message, options = {}) {
    return this.show(message, 'info', options);
  }

  /**
   * Show notification
   */
  show (message, type = 'info', options = {}) {
    if (!this.container) {
      console.warn('Notification container not initialized');
      return null;
    }

    const config = {
      timeout: this.defaultTimeout,
      persistent: false,
      actions: [],
      ...options
    };

    const toastConfig = this.notificationTypes[type] || this.notificationTypes.info;

    // Create toast element
    const toast = this.createToastElement(message, type, config);

    // Add to container
    this.container.appendChild(toast);

    // Manage toast queue
    this.addToasts(toast);

    // Show animation
    this.showToast(toast);

    // Set timeout for removal
    if (!config.persistent) {
      setTimeout(() => {
        this.removeToast(toast);
      }, toastConfig.timeout);
    }

    return toast;
  }

  /**
   * Create toast element
   */
  createToastElement (message, type, config) {
    const toast = document.createElement('div');
    const toastConfig = this.notificationTypes[type] || this.notificationTypes.info;

    toast.className = `toast ${toastConfig.className}`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');

    // Create toast content
    const content = document.createElement('div');
    content.className = 'toast-content';

    // Icon
    const icon = document.createElement('span');
    icon.className = 'toast-icon';
    icon.textContent = toastConfig.icon;
    content.appendChild(icon);

    // Message
    const messageElement = document.createElement('span');
    messageElement.className = 'toast-message';
    messageElement.textContent = message;
    content.appendChild(messageElement);

    toast.appendChild(content);

    // Actions (close button, custom actions)
    if (config.actions.length > 0) {
      const actionsContainer = document.createElement('div');
      actionsContainer.className = 'toast-actions';

      config.actions.forEach(action => {
        const button = document.createElement('button');
        button.className = 'toast-action';
        button.textContent = action.text;
        button.addEventListener('click', () => {
          if (action.handler) action.handler();
          if (action.closeOnClick !== false) {
            this.removeToast(toast);
          }
        });
        actionsContainer.appendChild(button);
      });

      toast.appendChild(actionsContainer);
    }

    // Default close button
    const closeButton = document.createElement('button');
    closeButton.className = 'toast-close';
    closeButton.setAttribute('aria-label', 'Close notification');
    closeButton.innerHTML = '&times;';
    closeButton.addEventListener('click', () => {
      this.removeToast(toast);
    });
    toast.appendChild(closeButton);

    return toast;
  }

  /**
   * Show toast with animation
   */
  showToast (toast) {
    // Force repaint
    toast.offsetHeight; // eslint-disable-line no-unused-expressions

    // Add show class
    toast.classList.add('toast-show');
  }

  /**
   * Remove toast with animation
   */
  removeToast (toast) {
    if (!toast) return;

    // Add hide class
    toast.classList.add('toast-hide');

    // Remove after animation
    setTimeout(() => {
      if (toast.parentNode) {
        toast.parentNode.removeChild(toast);
      }
      this.removeFromToasts(toast);
    }, 300);
  }

  /**
   * Add toast to active toasts list
   */
  addToasts (toast) {
    this.toasts.push(toast);

    // Remove oldest toast if exceeding max
    if (this.toasts.length > this.maxToasts) {
      this.removeToast(this.toasts[0]);
    }
  }

  /**
   * Remove toast from active toasts list
   */
  removeFromToasts (toast) {
    const index = this.toasts.indexOf(toast);
    if (index > -1) {
      this.toasts.splice(index, 1);
    }
  }

  /**
   * Clear all notifications
   */
  clear () {
    const toastsCopy = [...this.toasts];
    toastsCopy.forEach(toast => {
      this.removeToast(toast);
    });
  }

  /**
   * Show loading notification
   */
  loading (message = 'Loading...', options = {}) {
    const loadingToast = this.show(message, 'info', {
      persistent: true,
      ...options
    });

    // add loading animation
    if (loadingToast) {
      const icon = loadingToast.querySelector('.toast-icon');
      if (icon) {
        icon.classList.add('toast-loading');
        icon.innerHTML = '';
      }
    }

    return loadingToast;
  }

  /**
   * Update loading message
   */
  updateLoading (toast, message) {
    if (!toast) return;

    const messageElement = toast.querySelector('.toast-message');
    if (messageElement) {
      messageElement.textContent = message;
    }
  }

  /**
   * Complete loading notification
   */
  completeLoading (toast, message = 'Complete!', type = 'success', timeout = 2000) {
    if (!toast) return;

    // Update message
    this.updateLoading(toast, message);

    // Update type
    const toastConfig = this.notificationTypes[type] || this.notificationTypes.success;
    toast.className = `toast ${toastConfig.className}`;

    const icon = toast.querySelector('.toast-icon');
    if (icon) {
      icon.classList.remove('toast-loading');
      icon.textContent = toastConfig.icon;
    }

    // Set timeout for removal
    setTimeout(() => {
      this.removeToast(toast);
    }, timeout);
  }

  /**
   * Show progress notification
   */
  progress (message, current, total, options = {}) {
    const progressToast = this.show(message, 'info', {
      persistent: true,
      ...options
    });

    if (progressToast) {
      // Add progress bar
      const progressBar = document.createElement('div');
      progressBar.className = 'toast-progress';

      const progressFill = document.createElement('div');
      progressFill.className = 'toast-progress-fill';

      progressBar.appendChild(progressFill);
      progressToast.appendChild(progressBar);

      // Update progress
      this.updateProgress(progressToast, current, total);
    }

    return progressToast;
  }

  /**
   * Update progress notification
   */
  updateProgress (toast, current, total) {
    if (!toast) return;

    const progressFill = toast.querySelector('.toast-progress-fill');
    if (progressFill) {
      const percentage = Math.min(Math.max((current / total) * 100, 0), 100);
      progressFill.style.width = `${percentage}%`;
    }

    const messageElement = toast.querySelector('.toast-message');
    if (messageElement) {
      messageElement.textContent = `${messageElement.textContent.split('(')[0].trim()} (${current}/${total})`;
    }
  }

  /**
   * Show confirmation dialog as toast
   */
  confirm (message, confirmText = 'Yes', cancelText = 'No') {
    return new Promise((resolve) => {
      this.show(message, 'warning', {
        persistent: true,
        actions: [
          {
            text: cancelText,
            handler: () => resolve(false),
            closeOnClick: true
          },
          {
            text: confirmText,
            handler: () => resolve(true),
            closeOnClick: true
          }
        ]
      });
    });
  }

  /**
   * Show notification with custom HTML content
   */
  html (content, type = 'info', options = {}) {
    if (!this.container) return null;

    const config = {
      timeout: this.defaultTimeout,
      persistent: false,
      ...options
    };

    const toast = document.createElement('div');
    const toastConfig = this.notificationTypes[type] || this.notificationTypes.info;

    toast.className = `toast ${toastConfig.className}`;
    toast.setAttribute('role', 'alert');
    toast.setAttribute('aria-live', 'polite');

    toast.innerHTML = content;

    // Add close button if not persistent
    if (!config.persistent) {
      const closeButton = document.createElement('button');
      closeButton.className = 'toast-close';
      closeButton.setAttribute('aria-label', 'Close notification');
      closeButton.innerHTML = '&times;';
      closeButton.addEventListener('click', () => {
        this.removeToast(toast);
      });
      toast.appendChild(closeButton);
    }

    this.container.appendChild(toast);
    this.addToasts(toast);
    this.showToast(toast);

    if (!config.persistent) {
      setTimeout(() => {
        this.removeToast(toast);
      }, toastConfig.timeout);
    }

    return toast;
  }

  /**
   * Set max number of toasts
   */
  setMaxToasts (max) {
    this.maxToasts = Math.max(1, max);

    // Remove excess toasts
    while (this.toasts.length > this.maxToasts) {
      this.removeToast(this.toasts[0]);
    }
  }

  /**
   * Get active toasts count
   */
  getActiveCount () {
    return this.toasts.length;
  }

  /**
   * Check if service is initialized
   */
  isInitialized () {
    return !!this.container;
  }
}
